import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Check,
  ChevronRight,
  ChevronLeft,
  Upload,
  List,
  Table,
  Settings,
  CheckCircle2,
  Loader2,
  Car,
  Gauge,
  Ruler,
  Flag
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

const steps = [
  {
    id: 1,
    title: "Market & Source",
    description: "Choose your market and source",
    icon: Upload
  },
  {
    id: 2,
    title: "Data Content",
    description: "Select output format",
    icon: Table
  },
  {
    id: 3,
    title: "Configuration",
    description: "Additional settings",
    icon: Settings
  },
  {
    id: 4,
    title: "Review",
    description: "Confirm your request",
    icon: CheckCircle2
  }
];

type Market = {
  country: string;
};

type Source = {
  source_domain: string;
};

interface ModelConfiguration {
  manufacturer?: string;
  model?: string;
  year?: string | number;
  engine?: string;
  transmission?: string;
  dimensions?: Record<string, string | number>;
  features?: string[];
  power?: string;
  displacement?: string;
  fuel_type?: string;
  body_type?: string;
  drive_type?: string;
  tecdoc?: {
    v_id: string;
    maker_id: string;
    model_id: string;
  };
  total_articles?: number;
}

export default function DataRequest() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedMarket, setSelectedMarket] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [markets, setMarkets] = useState<Market[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [selectedTab, setSelectedTab] = useState("model");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [configurationData, setConfigurationData] = useState<ModelConfiguration | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [acknowledgeCredits, setAcknowledgeCredits] = useState(false);
  const { toast } = useToast();
  
  const progress = (currentStep / steps.length) * 100;

  useEffect(() => {
    async function fetchMarkets() {
      try {
        const { data, error } = await supabase
          .from('market_countries')
          .select('country')
          .order('country');
        
        if (error) {
          console.error('Error fetching markets:', error);
          return;
        }

        setMarkets(data || []);
      } catch (error) {
        console.error('Error fetching markets:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMarkets();
  }, []);

  useEffect(() => {
    async function fetchSources() {
      if (!selectedMarket) return;
      
      setIsLoadingSources(true);
      try {
        const { data, error } = await supabase
          .from('market_sources')
          .select('source_domain')
          .eq('country', selectedMarket)
          .order('source_domain');
        
        if (error) {
          console.error('Error fetching sources:', error);
          return;
        }

        setSources(data || []);
      } catch (error) {
        console.error('Error fetching sources:', error);
      } finally {
        setIsLoadingSources(false);
      }
    }

    fetchSources();
  }, [selectedMarket]);

  const nextStep = () => {
    if (currentStep < steps.length) {
      const newStep = currentStep + 1;
      if (newStep === 3 && selectedTab === "model" && url && !urlError) {
        fetchModelConfiguration();
      }
      if (currentStep === 3) {
        toast({
          title: "Request Processing",
          description: `Your request is being processed. You'll get notified when the data is ready in the data explorer.`,
          duration: 10000,
        });
      }
      setCurrentStep(newStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const getCountryCode = (country: string) => {
    const countryMap: { [key: string]: string } = {
      'Germany': 'de',
      'Austria': 'at',
      'France': 'fr',
      'Italy': 'it',
      'Spain': 'es',
      'Netherlands': 'nl',
      'Belgium': 'be',
      'Switzerland': 'ch',
      'United Kingdom': 'gb',
      'Poland': 'pl',
      'Czech Republic': 'cz',
      'Denmark': 'dk',
      'Sweden': 'se',
      'Norway': 'no',
      'Finland': 'fi'
    };
    
    return countryMap[country] || country.toLowerCase().substring(0, 2);
  };

  const validateUrl = (inputUrl: string) => {
    try {
      const urlObj = new URL(inputUrl);
      if (!selectedSource) {
        setUrlError("Please select a source domain first");
        return false;
      }
      if (!urlObj.hostname.includes(selectedSource)) {
        setUrlError(`URL must contain the selected domain: ${selectedSource}`);
        return false;
      }
      setUrlError("");
      return true;
    } catch {
      setUrlError("Please enter a valid URL");
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputUrl = e.target.value;
    setUrl(inputUrl);
    if (inputUrl) {
      validateUrl(inputUrl);
    } else {
      setUrlError("");
    }
  };

  const fetchModelConfiguration = async () => {
    setIsLoadingConfig(true);
    try {
      const { data, error } = await supabase.functions.invoke('modeldiscover', {
        body: { url }
      });
      
      if (error) throw error;
      console.log('Raw API Response:', data);

      if (data?.car_info) {
        const attributes: Record<string, string> = {};
        if (Array.isArray(data.car_info.model_attributes)) {
          data.car_info.model_attributes.forEach((attr: Record<string, unknown>) => {
            const entries = Object.entries(attr);
            if (entries.length > 0) {
              const [key, value] = entries[0]; // Each attribute has only one key-value pair
              const cleanKey = key.replace(':', '').trim();
              attributes[cleanKey] = String(value).trim();
            }
          });
        }

        console.log('Parsed attributes:', attributes);

        const transformedData: ModelConfiguration = {
          manufacturer: data.car_info.model_name?.split(' ')[0],
          model: data.car_info.model_name?.split(' ').slice(0, 2).join(' '),
          year: attributes['Baujahr (von-bis)']?.split('-')[0]?.trim(),
          engine: attributes['Motorcode'],
          transmission: attributes['Getriebecode'],
          power: `${attributes['Leistung [PS]']} PS / ${attributes['Leistung [kW]']} kW`,
          displacement: attributes['Hubraum (ccm)'],
          fuel_type: attributes['Kraftstoffart'],
          body_type: attributes['Karosserietyp'],
          drive_type: attributes['Antriebsart'],
          tecdoc: {
            v_id: data.car_info.model_tecdoc_v_id,
            maker_id: data.car_info.model_tecdoc_maker_id,
            model_id: data.car_info.model_tecdoc_model_id
          },
          total_articles: data.total_articles
        };

        console.log('Final transformed data:', transformedData);
        setConfigurationData(transformedData);
      } else {
        console.log('No car_info in response:', data);
        setConfigurationData(null);
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
      setConfigurationData(null);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Select Market</h3>
              <p className="text-muted-foreground">
                Choose the market you want to request data from.
              </p>
              <div className="w-full max-w-xs">
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading markets...</span>
                  </div>
                ) : (
                  <Select value={selectedMarket} onValueChange={setSelectedMarket}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a market" />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map((market) => (
                        <SelectItem key={market.country} value={market.country}>
                          <div className="flex items-center gap-2">
                            <img 
                              src={`https://flagcdn.com/24x18/${getCountryCode(market.country)}.png`}
                              alt={`${market.country} flag`}
                              className="w-6 h-4 object-cover rounded"
                            />
                            <span>{market.country}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Select Source</h3>
              <p className="text-muted-foreground">
                Choose where you want to source your data from.
              </p>
              <div className="w-full max-w-xs">
                {isLoadingSources ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading sources...</span>
                  </div>
                ) : (
                  <Select 
                    value={selectedSource} 
                    onValueChange={setSelectedSource}
                    disabled={!selectedMarket}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={selectedMarket ? "Select a source" : "Select a market first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {sources.map((source) => (
                        <SelectItem 
                          key={source.source_domain} 
                          value={source.source_domain}
                        >
                          {source.source_domain}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Data Content</h3>
            <p className="text-muted-foreground">
              Select how you want your data to be formatted.
            </p>
            <Tabs
              defaultValue="model"
              value={selectedTab}
              onValueChange={setSelectedTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="model">Model</TabsTrigger>
                <TabsTrigger value="category">Category</TabsTrigger>
                <TabsTrigger value="brand">Brand</TabsTrigger>
              </TabsList>
              <TabsContent value="model" className="space-y-4">
                <div className="rounded-lg border p-4">
                  <h4 className="font-medium mb-2">Model Data</h4>
                  <p className="text-muted-foreground mb-4">
                    Configure options for model-specific data.
                  </p>
                  <div className="space-y-2">
                    <label htmlFor="url" className="text-sm font-medium">
                      Product URL
                    </label>
                    <div className="relative">
                      <Input
                        id="url"
                        type="url"
                        placeholder="Enter product URL"
                        value={url}
                        onChange={handleUrlChange}
                        className={`${urlError ? "border-red-500" : ""} pr-8`}
                      />
                      {url && !urlError && (
                        <CheckCircle2 className="absolute right-2 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                      )}
                    </div>
                    {urlError && (
                      <p className="text-sm text-red-500">{urlError}</p>
                    )}
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="category" className="space-y-4">
                <div className="rounded-lg border p-4">
                  <h4 className="font-medium mb-2">Category Data</h4>
                  <p className="text-muted-foreground">
                    Configure options for category-specific data.
                  </p>
                </div>
              </TabsContent>
              <TabsContent value="brand" className="space-y-4">
                <div className="rounded-lg border p-4">
                  <h4 className="font-medium mb-2">Brand Data</h4>
                  <p className="text-muted-foreground">
                    Configure options for brand-specific data.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Model Information</h3>
            <p className="text-muted-foreground">
              Review the extracted vehicle information below.
            </p>
            {selectedTab === "model" && (
              <div className="mt-4">
                {isLoadingConfig ? (
                  <div className="flex flex-col items-center justify-center p-8 space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <p className="text-muted-foreground">Fetching model information...</p>
                  </div>
                ) : configurationData ? (
                  <>
                    <div className="rounded-lg border p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="p-4 space-y-3">
                          <div className="flex items-center space-x-2 text-primary">
                            <Car className="h-5 w-5" />
                            <h4 className="font-semibold">Basic Information</h4>
                          </div>
                          <div className="space-y-2">
                            {configurationData.manufacturer && (
                              <div className="flex items-center space-x-2">
                                <Flag className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Manufacturer:</span>{' '}
                                  {configurationData.manufacturer}
                                </span>
                              </div>
                            )}
                            {configurationData.model && (
                              <div className="flex items-center space-x-2">
                                <Car className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Model:</span>{' '}
                                  {configurationData.model}
                                </span>
                              </div>
                            )}
                            {configurationData.year && (
                              <div className="flex items-center space-x-2">
                                <span className="text-sm">
                                  <span className="font-medium">Year:</span>{' '}
                                  {String(configurationData.year)}
                                </span>
                              </div>
                            )}
                            {configurationData.body_type && (
                              <div className="flex items-center space-x-2">
                                <Car className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Body Type:</span>{' '}
                                  {configurationData.body_type}
                                </span>
                              </div>
                            )}
                          </div>
                        </Card>

                        <Card className="p-4 space-y-3">
                          <div className="flex items-center space-x-2 text-primary">
                            <Gauge className="h-5 w-5" />
                            <h4 className="font-semibold">Technical Specifications</h4>
                          </div>
                          <div className="space-y-2">
                            {configurationData.power && (
                              <div className="flex items-center space-x-2">
                                <Gauge className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Power:</span>{' '}
                                  {configurationData.power}
                                </span>
                              </div>
                            )}
                            {configurationData.engine && (
                              <div className="flex items-center space-x-2">
                                <Gauge className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Engine Code:</span>{' '}
                                  {configurationData.engine}
                                </span>
                              </div>
                            )}
                            {configurationData.displacement && (
                              <div className="flex items-center space-x-2">
                                <Gauge className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Displacement:</span>{' '}
                                  {configurationData.displacement} ccm
                                </span>
                              </div>
                            )}
                            {configurationData.fuel_type && (
                              <div className="flex items-center space-x-2">
                                <Gauge className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Fuel Type:</span>{' '}
                                  {configurationData.fuel_type}
                                </span>
                              </div>
                            )}
                          </div>
                        </Card>

                        <Card className="p-4 space-y-3">
                          <div className="flex items-center space-x-2 text-primary">
                            <Settings className="h-5 w-5" />
                            <h4 className="font-semibold">Drivetrain</h4>
                          </div>
                          <div className="space-y-2">
                            {configurationData.transmission && (
                              <div className="flex items-center space-x-2">
                                <Settings className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Transmission:</span>{' '}
                                  {configurationData.transmission}
                                </span>
                              </div>
                            )}
                            {configurationData.drive_type && (
                              <div className="flex items-center space-x-2">
                                <Settings className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Drive Type:</span>{' '}
                                  {configurationData.drive_type}
                                </span>
                              </div>
                            )}
                          </div>
                        </Card>

                        <Card className="p-4 space-y-3">
                          <div className="flex items-center space-x-2 text-primary">
                            <List className="h-5 w-5" />
                            <h4 className="font-semibold">TecDoc Information</h4>
                          </div>
                          <div className="space-y-2">
                            {configurationData.tecdoc?.v_id && (
                              <div className="flex items-center space-x-2">
                                <List className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Vehicle ID:</span>{' '}
                                  {configurationData.tecdoc.v_id}
                                </span>
                              </div>
                            )}
                            {configurationData.tecdoc?.maker_id && (
                              <div className="flex items-center space-x-2">
                                <List className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Maker ID:</span>{' '}
                                  {configurationData.tecdoc.maker_id}
                                </span>
                              </div>
                            )}
                            {configurationData.tecdoc?.model_id && (
                              <div className="flex items-center space-x-2">
                                <List className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">Model ID:</span>{' '}
                                  {configurationData.tecdoc.model_id}
                                </span>
                              </div>
                            )}
                          </div>
                        </Card>
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                      <p className="text-center text-primary font-medium">
                        We found {Number(configurationData?.total_articles).toLocaleString() || '0'} total articles for {configurationData?.model || 'this model'} in the {selectedMarket} market on {selectedSource}
                      </p>
                    </div>
                    
                    <div className="mt-6 space-y-6">
                      <div className="rounded-lg border p-4 bg-muted/50">
                        <h4 className="font-medium mb-2">Process Information</h4>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <p>
                            Estimated processing time:{' '}
                            {configurationData.total_articles ? 
                              `${Math.max(1, Math.ceil(((configurationData.total_articles / 20) * 10) / 60))} minutes` : 
                              'Unknown'}
                          </p>
                          <p>
                            Required credits:{' '}
                            {configurationData.total_articles ? 
                              `${(configurationData.total_articles * 3).toLocaleString()} credits` : 
                              'Unknown'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="acknowledge"
                          checked={acknowledgeCredits}
                          onCheckedChange={(checked) => setAcknowledgeCredits(checked as boolean)}
                          className="mt-1"
                        />
                        <label
                          htmlFor="acknowledge"
                          className="text-sm text-muted-foreground leading-relaxed"
                        >
                          I acknowledge that proceeding to the next step will consume{' '}
                          {(Number(configurationData?.total_articles) * 3).toLocaleString()} credits from my account.
                          This process cannot be undone and credits will not be refunded once the process starts.
                        </label>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground">
                    No model information available. Please enter a valid product URL.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Review Request</h3>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Check className="h-5 w-5 text-green-500" />
              <p>Your request is being processed now. You'll get notified when the data is ready in the data explorer.</p>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border p-4">
                <h4 className="font-medium mb-2">Request Details</h4>
                <div className="space-y-2">
                  <p><strong>Selected Market:</strong> {selectedMarket}</p>
                  <p><strong>Selected Source:</strong> {selectedSource}</p>
                  <p><strong>Data Type:</strong> {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}</p>
                  {selectedTab === "model" && url && (
                    <p><strong>Product URL:</strong> {url}</p>
                  )}
                </div>
              </div>

              {configurationData && (
                <>
                  <div className="rounded-lg border p-4 bg-primary/10">
                    <p className="text-center text-primary font-medium">
                      We found {Number(configurationData?.total_articles).toLocaleString() || '0'} total articles for {configurationData?.model || 'this model'} in the {selectedMarket} market on {selectedSource}
                    </p>
                  </div>

                  <div className="rounded-lg border p-4 bg-muted/50">
                    <h4 className="font-medium mb-2">Process Information</h4>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>
                        Estimated processing time:{' '}
                        {configurationData.total_articles ? 
                          `${Math.max(1, Math.ceil(((configurationData.total_articles / 20) * 10) / 60))} minutes` : 
                          'Unknown'}
                      </p>
                      <p>
                        Required credits:{' '}
                        {configurationData.total_articles ? 
                          `${(configurationData.total_articles * 3).toLocaleString()} credits` : 
                          'Unknown'}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container py-10">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Data Request</h2>
          <p className="text-muted-foreground mt-2">
            Complete the following steps to request new data
          </p>
        </div>

        <Progress value={progress} className="h-2" />

        <div className="flex gap-4 items-center justify-between mb-8">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`flex flex-col items-center space-y-2 flex-1 ${
                step.id === currentStep
                  ? "text-primary"
                  : step.id < currentStep
                  ? "text-muted-foreground"
                  : "text-muted-foreground/50"
              }`}
            >
              <div className={`rounded-full p-2 ${
                step.id === currentStep
                  ? "bg-primary text-primary-foreground"
                  : step.id < currentStep
                  ? "bg-muted"
                  : "bg-muted/50"
              }`}>
                {step.id < currentStep ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <step.icon className="h-5 w-5" />
                )}
              </div>
              <span className="text-sm font-medium hidden md:block">{step.title}</span>
            </div>
          ))}
        </div>

        <Card className="p-6">
          {renderStepContent(currentStep)}

          <Separator className="my-6" />

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Previous
            </Button>
            {currentStep !== steps.length && (
              <Button
                onClick={nextStep}
                disabled={
                  (currentStep === 1 && (!selectedMarket || !selectedSource)) ||
                  (currentStep === 3 && !acknowledgeCredits)
                }
              >
                {currentStep === 3 ? (
                  <>
                    Submit <Check className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Next <ChevronRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
